import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiLock, FiCheckCircle, FiAlertTriangle, FiTrendingUp } = FiIcons;

const HeroSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    // SEO: Add structured data for hero section
    const heroData = {
      "@context": "https://schema.org",
      "@type": "WebPageElement",
      "name": "Zardoz.ai - Zero-Audit Resilient Defense Over Zero-Knowledge",
      "description": "The god of unseen defenses. Secure, private enterprise AI solutions you fully control with zero-audit, zero-knowledge architecture.",
      "text": "ZARDOZ: Zero-Audit Resilient Defense Over Zero-Knowledge. Stop Risking Your Data to Public AI Clouds."
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(heroData);
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const stats = [
    { icon: FiShield, value: "100%", label: "Private Deployment" },
    { icon: FiLock, value: "Zero", label: "Third-Party Exposure" },
    { icon: FiCheckCircle, value: "40+", label: "Years Security Expertise" },
    { icon: FiTrendingUp, value: "24/7", label: "Resilient Defense" }
  ];

  return (
    <section className="hero-section hero-gradient relative" itemScope itemType="https://schema.org/WebPageElement">
      {/* Background Pattern */}
      <div className="hero-bg-pattern" />
      
      {/* Floating Shapes */}
      <div className="floating-shape shape-1" />
      <div className="floating-shape shape-2" />
      <div className="floating-shape shape-3" />
      
      <div className="container">
        <motion.div 
          className="hero-content max-w-5xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 40 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6"
          >
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 backdrop-blur-sm">
              <SafeIcon icon={FiShield} className="text-teal-400 mr-2" />
              <span className="text-teal-300 text-sm font-medium tracking-wider">ENTERPRISE AI SECURITY GUARDIAN</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            itemProp="headline"
          >
            ZARDOZ.ai
          </motion.h1>
          
          <motion.h2
            className="text-2xl md:text-3xl font-light text-teal-400 mb-8 tracking-wide"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <span className="font-bold">Z</span>ero-<span className="font-bold">A</span>udit <span className="font-bold">R</span>esilient <span className="font-bold">D</span>efense <span className="font-bold">O</span>ver <span className="font-bold">Z</span>ero-Knowledge
          </motion.h2>

          {/* Sub-headline */}
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            itemProp="description"
          >
            The god of unseen defenses. Stop risking your data to public AI clouds.
            <span className="block text-white font-semibold mt-2">No Traces. No Leaks. Absolute Control.</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 mb-12 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
          >
            <a 
              href="#consultation" 
              className="btn-primary text-center"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('whitepaper')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <SafeIcon icon={FiShield} className="inline-block mr-2" />
              Request Guarded Access
            </a>
            <a 
              href="#whitepaper" 
              className="btn-secondary text-center"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('whitepaper')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <SafeIcon icon={FiLock} className="inline-block mr-2" />
              Download Protocol Brief
            </a>
          </motion.div>

          {/* Trust Badges */}
          <motion.div 
            className="trust-badges justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.8 }}
          >
            <div className="trust-badge">
              <SafeIcon icon={FiCheckCircle} className="text-teal-400" />
              <span>GDRP Compliant</span>
            </div>
            <div className="trust-badge">
              <SafeIcon icon={FiCheckCircle} className="text-teal-400" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="trust-badge">
              <SafeIcon icon={FiCheckCircle} className="text-teal-400" />
              <span>PCI DSS Ready</span>
            </div>
          </motion.div>

          {/* Key Stats Grid */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
                itemScope
                itemType="https://schema.org/QuantitativeValue"
              >
                <div className="w-16 h-16 bg-teal-500/10 rounded-lg flex items-center justify-center mx-auto mb-3 border border-teal-500/20">
                  <SafeIcon icon={stat.icon} className="text-teal-400 text-2xl" />
                </div>
                <div className="text-3xl font-bold text-white mb-1" itemProp="value">{stat.value}</div>
                <div className="text-gray-400 text-sm px-2" itemProp="name">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;